import React, { useState, useEffect } from 'react';
import { 
  Building2, Wallet, ArrowUpRight, ArrowDownRight, 
  RefreshCw, AlertCircle, CheckCircle2, Settings, IndianRupee
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { api } from '../lib/api';
import { toast } from 'sonner';
import PINGate from '../components/PINGate';

const FinanceDashboard = () => {
  const [tallyStatus, setTallyStatus] = useState(null);
  const [financeSummary, setFinanceSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pinVerified, setPinVerified] = useState(false);

  useEffect(() => {
    if (pinVerified) {
      fetchData();
    }
  }, [pinVerified]);

  const fetchData = async () => {
    try {
      const [statusRes, summaryRes] = await Promise.all([
        api.getTallyStatus(),
        api.getFinanceSummary()
      ]);
      setTallyStatus(statusRes.data);
      setFinanceSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  };

  const FinanceContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Finance Dashboard</h1>
            <p className="text-slate-500">Financial Overview & Tally Integration</p>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Tally Connection Status */}
        <Card className={tallyStatus?.connected ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Tally Prime Connection
              </span>
              <Badge className={tallyStatus?.connected ? 'bg-green-600' : 'bg-amber-600'}>
                {tallyStatus?.connected ? (
                  <>
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Connected
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Not Connected
                  </>
                )}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tallyStatus?.connected ? (
              <p className="text-green-800">Tally Prime is connected and syncing data.</p>
            ) : (
              <div className="space-y-4">
                <p className="text-amber-800">{tallyStatus?.message}</p>
                
                <div className="bg-white p-4 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-slate-900 mb-3">Configuration Required:</h4>
                  <ul className="space-y-2 text-sm">
                    {tallyStatus?.required_config && Object.entries(tallyStatus.required_config).map(([key, value]) => (
                      <li key={key} className="flex items-start gap-2">
                        <Settings className="h-4 w-4 text-slate-400 mt-0.5" />
                        <div>
                          <span className="font-medium text-slate-700 capitalize">{key.replace(/_/g, ' ')}:</span>
                          <span className="text-slate-500 ml-2">{value}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">How to Connect Tally Prime:</h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                    <li>Open Tally Prime on your server</li>
                    <li>Go to Gateway → Configure → Connectivity</li>
                    <li>Enable "Tally.NET Server" and set port to 9000</li>
                    <li>Note down your server IP address</li>
                    <li>Contact admin to configure the connection</li>
                  </ol>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Finance Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Receivables</CardTitle>
              <ArrowDownRight className="h-5 w-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">
                ₹{(financeSummary?.placeholder_data?.receivables || 0).toLocaleString()}
              </div>
              <p className="text-xs text-slate-500 mt-1">Amount to receive</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Payables</CardTitle>
              <ArrowUpRight className="h-5 w-5 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">
                ₹{(financeSummary?.placeholder_data?.payables || 0).toLocaleString()}
              </div>
              <p className="text-xs text-slate-500 mt-1">Amount to pay</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Cash Balance</CardTitle>
              <Wallet className="h-5 w-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">
                ₹{(financeSummary?.placeholder_data?.cash_balance || 0).toLocaleString()}
              </div>
              <p className="text-xs text-slate-500 mt-1">Cash in hand</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Bank Balance</CardTitle>
              <Building2 className="h-5 w-5 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">
                ₹{(financeSummary?.placeholder_data?.bank_balance || 0).toLocaleString()}
              </div>
              <p className="text-xs text-slate-500 mt-1">Bank accounts</p>
            </CardContent>
          </Card>
        </div>

        {/* Placeholder for future features */}
        <Card>
          <CardHeader>
            <CardTitle>Coming Soon</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 opacity-50">
                <h3 className="font-semibold text-slate-900 mb-2">Ledger View</h3>
                <p className="text-sm text-slate-600">View detailed ledger balances from Tally</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 opacity-50">
                <h3 className="font-semibold text-slate-900 mb-2">Invoice Tracking</h3>
                <p className="text-sm text-slate-600">Track pending and paid invoices</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 opacity-50">
                <h3 className="font-semibold text-slate-900 mb-2">P&L Reports</h3>
                <p className="text-sm text-slate-600">Profit & Loss statements from Tally</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <PINGate module="finance" onVerified={() => setPinVerified(true)}>
      <FinanceContent />
    </PINGate>
  );
};

export default FinanceDashboard;
